import { useEffect, useState, Fragment } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserPlus, Trash2, Key, Activity, Shield, LayoutGrid, CheckCircle, XCircle, Circle, Settings2, Eye, EyeOff, Edit, Link2, Copy, Check, ChevronDown, ChevronUp, Mail, RotateCw, Ban, Clock, Phone, User as UserIcon, Calendar, QrCode, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { QRCodeCanvas } from 'qrcode.react';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { formatDate, formatDateTime } from '@/utils/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const AVAILABLE_TABS = [
  { id: '/pen-production', label: 'Pen Production', roles: ['staff', 'manager', 'support_staff'] },
  { id: '/production', label: 'Daily Inventory', roles: ['staff', 'manager', 'support_staff'] },
  { id: '/pen-floor-production', label: 'Pen Floor Daily Egg Production & Mortalities', roles: ['staff', 'manager', 'support_staff'] },
  { id: '/customer-order-pickup', label: 'Customer Order Pick-up', roles: ['staff', 'manager', 'support_staff'] },
  { id: '/chicken-inventory', label: 'Chicken Demographic Records', roles: ['staff', 'manager', 'support_staff'] },
  { id: '/chicken-census', label: 'Chicken Census', roles: ['staff', 'manager', 'support_staff'] },
  { id: '/chicken-feed-audit', label: 'Chicken Feed Audit', roles: ['staff', 'manager', 'support_staff'] },
  { id: '/frozen-chicken-count', label: 'Frozen Chicken Count', roles: ['staff', 'manager', 'support_staff'] },
  { id: '/reports', label: 'Reports', roles: ['staff', 'manager', 'support_staff'] },
  { id: '/order-notifications', label: 'Order Notification To Farm', roles: ['staff', 'manager', 'distributor', 'support_staff'] },
  { id: '/order-request', label: 'Customer Order Request', roles: ['customer', 'distributor', 'manager', 'support_staff'] },
  { id: '/customer-account-records', label: 'Customer Account Records', roles: ['staff', 'manager', 'support_staff'] },
  { id: '/forms', label: 'Forms', roles: ['staff', 'manager', 'support_staff'] },
  { id: '/timesheet', label: 'Time Sheet', roles: ['staff', 'manager', 'support_staff'] },
  { id: '/messages', label: 'Messages', roles: ['staff', 'manager', 'distributor', 'vet_doctor', 'support_staff'] },
  { id: '/payslip', label: 'Pay Slip', roles: ['staff', 'manager', 'support_staff'] },
  { id: '/directory', label: 'Directory', roles: ['staff', 'manager', 'support_staff'] },
  { id: '/vet-reports', label: 'Vet Reports', roles: ['vet_doctor', 'manager'] },
  { id: '/customers', label: 'Customers', roles: ['manager', 'support_staff'] },
  { id: '/access-bank', label: 'Access Bank (view only)', roles: ['manager'] },
  { id: '/zenith-bank', label: 'Zenith Bank (view only)', roles: ['manager'] },
  { id: '/other-bank', label: 'Other Bank (view only)', roles: ['manager'] }
];

// Get available tabs based on user role
const getAvailableTabsForRole = (role) => {
  if (!role) return [];
  return AVAILABLE_TABS.filter(tab => tab.roles.includes(role));
};

const STAFF_PERMISSIONS = [
  { id: 'can_add_order_notification', label: 'Add Order (Order Notification To Farm)' },
  { id: 'can_add_fulfilment', label: 'Add Fulfilment (Order Fulfilment)' }
];

const RegistrationLinksPanel = () => {
  const [open, setOpen] = useState(true);
  const [links, setLinks] = useState([]);
  const [copied, setCopied] = useState(null);
  const [busy, setBusy] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailRole, setEmailRole] = useState(null);
  const [emailValue, setEmailValue] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [qrLink, setQrLink] = useState(null);

  const fetchLinks = async () => {
    try {
      const res = await axios.get(`${API}/registration-links`, getAuthHeader());
      setLinks(res.data);
    } catch (e) { /* non-admins ignored */ }
  };
  useEffect(() => { fetchLinks(); }, []);

  const buildUrl = (l) => `${window.location.origin}/register/${l.role}?k=${l.token}`;

  const copy = async (l) => {
    if (l.disabled) return toast.error('This link is disabled. Enable it first.');
    try {
      await navigator.clipboard.writeText(buildUrl(l));
      setCopied(l.role);
      toast.success(`${l.label} registration link copied`);
      setTimeout(() => setCopied(null), 2000);
    } catch (e) { toast.error('Could not copy link'); }
  };

  const updateLink = async (role, body, msg) => {
    setBusy(role);
    try {
      await axios.put(`${API}/registration-links/${role}`, body, getAuthHeader());
      if (msg) toast.success(msg);
      await fetchLinks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally { setBusy(null); }
  };

  const setExpiry = (l, val) => {
    if (val === 'never') updateLink(l.role, { clear_expiry: true }, `${l.label} link set to never expire`);
    else updateLink(l.role, { expires_days: parseInt(val) }, `${l.label} link expires in ${val} days`);
  };

  const openEmail = (l) => {
    if (l.disabled) return toast.error('This link is disabled. Enable it first.');
    setEmailRole(l); setEmailValue(''); setEmailOpen(true);
  };

  const openQr = (l) => {
    if (l.disabled) return toast.error('This link is disabled. Enable it first.');
    setQrLink(l);
  };

  const downloadQr = () => {
    const canvas = document.getElementById('registration-qr-canvas');
    if (!canvas) return;
    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `register-${qrLink.role}-qr.png`;
    a.click();
    toast.success('QR code downloaded');
  };
  const sendEmail = async () => {
    if (!emailValue.trim()) return toast.error('Enter an email address');
    setEmailSending(true);
    try {
      await axios.post(`${API}/registration-links/${emailRole.role}/email`, { email: emailValue.trim() }, getAuthHeader());
      toast.success(`Link sent to ${emailValue.trim()}`);
      setEmailOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send email');
    } finally { setEmailSending(false); }
  };

  if (links.length === 0) return null;

  const statusBadge = (l) => {
    if (l.disabled) return <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">Disabled</span>;
    if (l.expires_at) return <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">Expires {formatDate(l.expires_at)}</span>;
    return <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">Active</span>;
  };

  return (
    <div className="card p-5" data-testid="registration-links-panel">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between" data-testid="toggle-registration-links">
        <div className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center"><Link2 className="w-5 h-5" /></span>
          <div className="text-left">
            <h3 className="font-semibold text-slate-800">Registration Links</h3>
            <p className="text-xs text-slate-500">Copy or email a role-specific link. Set an expiry, rotate to kill old links, or disable a role. New sign-ups appear below for approval.</p>
          </div>
        </div>
        {open ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
      </button>

      {open && (
        <div className="mt-4 space-y-3">
          {links.map((l) => (
            <div key={l.role} className="border border-slate-200 rounded-xl p-3 bg-slate-50" data-testid={`reg-link-row-${l.role}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <p className="text-sm font-semibold text-slate-800">{l.label}</p>
                  {statusBadge(l)}
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Select value="" onValueChange={(v) => setExpiry(l, v)}>
                    <SelectTrigger className="h-8 w-[130px] text-xs" data-testid={`expiry-select-${l.role}`}>
                      <span className="flex items-center gap-1 text-slate-500"><Clock className="w-3.5 h-3.5" /> Set expiry</span>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="never">Never expire</SelectItem>
                      <SelectItem value="1">1 day</SelectItem>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" className="h-8 gap-1" disabled={busy === l.role} onClick={() => updateLink(l.role, { rotate: true }, `${l.label} link rotated — old links no longer work`)} data-testid={`rotate-link-${l.role}`}>
                    <RotateCw className="w-3.5 h-3.5" /> Rotate
                  </Button>
                  <Button size="sm" variant="outline" className={`h-8 gap-1 ${l.disabled ? 'text-emerald-700' : 'text-red-600'}`} disabled={busy === l.role} onClick={() => updateLink(l.role, { disabled: !l.disabled }, l.disabled ? `${l.label} link enabled` : `${l.label} link disabled`)} data-testid={`toggle-disable-${l.role}`}>
                    <Ban className="w-3.5 h-3.5" /> {l.disabled ? 'Enable' : 'Disable'}
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => openQr(l)} data-testid={`qr-link-${l.role}`}>
                    <QrCode className="w-3.5 h-3.5" /> QR
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => openEmail(l)} data-testid={`email-link-${l.role}`}>
                    <Mail className="w-3.5 h-3.5" /> Email
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => copy(l)} data-testid={`copy-reg-link-${l.role}`}>
                    {copied === l.role ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied === l.role ? 'Copied' : 'Copy'}
                  </Button>
                </div>
              </div>
              <p className={`text-xs mt-1.5 truncate ${l.disabled ? 'text-slate-400 line-through' : 'text-slate-400'}`}>{buildUrl(l)}</p>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!qrLink} onOpenChange={(v) => !v && setQrLink(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{qrLink?.label} Registration QR Code</DialogTitle>
          </DialogHeader>
          {qrLink && (
            <div className="flex flex-col items-center gap-4 py-2" data-testid="qr-dialog">
              <div className="p-4 bg-white rounded-xl border">
                <QRCodeCanvas id="registration-qr-canvas" value={buildUrl(qrLink)} size={220} level="M" includeMargin />
              </div>
              <p className="text-xs text-slate-500 text-center break-all">{buildUrl(qrLink)}</p>
              <p className="text-xs text-slate-400 text-center">Scan to open the {qrLink.label} registration form. Print it on notices or share on WhatsApp.</p>
              <Button onClick={downloadQr} className="w-full bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="download-qr-btn">
                <Download className="w-4 h-4" /> Download PNG
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Email {emailRole?.label} Registration Link</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Recipient email address</Label>
            <Input type="email" value={emailValue} onChange={(e) => setEmailValue(e.target.value)} placeholder="prospect@example.com" data-testid="email-link-input" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEmailOpen(false)}>Cancel</Button>
              <Button onClick={sendEmail} disabled={emailSending} className="bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="send-email-link-btn">
                {emailSending ? <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" /> : <Mail className="w-4 h-4" />}
                Send Link
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const DetailRow = ({ icon: Icon, label, value, capitalize }) => (
  <div className="flex items-center gap-3 px-3 py-2">
    <Icon className="w-4 h-4 text-slate-400 flex-shrink-0" />
    <span className="text-xs text-slate-500 w-24">{label}</span>
    <span className={`text-sm font-medium text-slate-800 ${capitalize ? 'capitalize' : ''}`}>{value}</span>
  </div>
);

const lengthOfService = (dateStr) => {
  if (!dateStr) return '—';
  const start = new Date(dateStr);
  if (isNaN(start.getTime())) return '—';
  const now = new Date();
  let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
  if (now.getDate() < start.getDate()) months -= 1;
  if (months < 0) return '—';
  const yrs = Math.floor(months / 12);
  const mths = months % 12;
  const parts = [];
  if (yrs > 0) parts.push(`${yrs} yr${yrs > 1 ? 's' : ''}`);
  parts.push(`${mths} mth${mths !== 1 ? 's' : ''}`);
  return parts.join(' ');
};


const Users = () => {
  const [users, setUsers] = useState([]);
  const [approveTarget, setApproveTarget] = useState(null);
  const [userActivities, setUserActivities] = useState({});
  const [onlineStatus, setOnlineStatus] = useState({});
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    date_of_hire: '',
    next_of_kin_name: '',
    next_of_kin_phone: '',
    starting_salary: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    profile_photo: ''
  });
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [roleChangeOpen, setRoleChangeOpen] = useState(false);
  const [adminCount, setAdminCount] = useState(0);
  const MAX_ADMINS = 5;
  const [tabsOpen, setTabsOpen] = useState(false);
  const [permissionsOpen, setPermissionsOpen] = useState(false);
  const [subTab, setSubTab] = useState('staff');
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('');
  const [selectedTabs, setSelectedTabs] = useState([]);
  const [staffPermissions, setStaffPermissions] = useState({
    can_add_order_notification: false,
    can_manage_feed_inventory: false,
    can_add_fulfilment: false
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    username: '',
    phone: '',
    role: 'staff',
    address: ''
  });

  useEffect(() => {
    fetchUsers();
    fetchOnlineStatus();
    // Refresh online status every 30 seconds
    const interval = setInterval(fetchOnlineStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchOnlineStatus = async () => {
    try {
      const response = await axios.get(`${API}/users/online-status`, getAuthHeader());
      setOnlineStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch online status');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, getAuthHeader());
      setUsers(response.data);
      // Fetch activity for each user
      for (const user of response.data) {
        fetchUserActivity(user.id);
      }
    } catch (error) {
      toast.error('Failed to fetch users');
    }
  };

  const fetchUserActivity = async (userId) => {
    try {
      const response = await axios.get(`${API}/users/${userId}/activity`, getAuthHeader());
      setUserActivities(prev => ({ ...prev, [userId]: response.data }));
    } catch (error) {
      console.error('Failed to fetch user activity');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Use admin endpoint to create user with temporary password
      const response = await axios.post(`${API}/admin/users/create`, {
        name: formData.name,
        email: formData.email || undefined,
        username: formData.username || undefined,
        phone: formData.phone || undefined,
        role: formData.role,
        address: formData.address || undefined
      }, getAuthHeader());
      
      // Show success message with temporary password info
      toast.success(
        <div>
          <p className="font-semibold">User created successfully!</p>
          <p className="text-sm mt-1">
            Temporary password: <span className="font-mono bg-slate-100 px-1 rounded">123456</span>
          </p>
          <p className="text-xs text-slate-500 mt-1">User must change password on first login</p>
        </div>,
        { duration: 8000 }
      );
      fetchUsers();
      handleClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setEditFormData({
      name: user.name || '',
      phone: user.phone || '',
      email: user.email || '',
      address: user.address || '',
      date_of_hire: user.date_of_hire || '',
      next_of_kin_name: user.next_of_kin_name || '',
      next_of_kin_phone: user.next_of_kin_phone || '',
      starting_salary: (user.starting_salary !== null && user.starting_salary !== undefined) ? String(user.starting_salary) : '',
      emergency_contact_name: user.emergency_contact_name || '',
      emergency_contact_phone: user.emergency_contact_phone || '',
      profile_photo: user.profile_photo || ''
    });
    setEditOpen(true);
  };

  const handleEditPhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Image must be under 5MB'); return; }
    const reader = new FileReader();
    reader.onload = () => setEditFormData((p) => ({ ...p, profile_photo: reader.result }));
    reader.readAsDataURL(file);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    try {
      const payload = {
        ...editFormData,
        starting_salary: editFormData.starting_salary === '' ? null : parseFloat(editFormData.starting_salary)
      };
      await axios.put(`${API}/users/${editingUser.id}/details`, payload, getAuthHeader());
      toast.success('User details updated successfully');
      setEditOpen(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await axios.delete(`${API}/users/${userId}`, getAuthHeader());
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handlePermissionToggle = async (userId, currentPermission) => {
    try {
      await axios.put(`${API}/users/${userId}/permissions?can_enter_production=${!currentPermission}`, {}, getAuthHeader());
      toast.success('Permissions updated successfully');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update permissions');
    }
  };

  const handleApproveUser = async (userId, userName) => {
    try {
      await axios.put(`${API}/users/${userId}/approve`, {}, getAuthHeader());
      toast.success(`${userName} has been approved`);
      setApproveTarget(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve user');
    }
  };

  const handleRevokeApproval = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to revoke approval for ${userName}?`)) return;
    try {
      await axios.put(`${API}/users/${userId}/revoke`, {}, getAuthHeader());
      toast.success(`Approval revoked for ${userName}`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke approval');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    try {
      await axios.put(`${API}/users/${selectedUser.id}/reset-password`, { new_password: newPassword }, getAuthHeader());
      toast.success(`Password reset successfully for ${selectedUser.name}`);
      setResetPasswordOpen(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleRoleChange = async (e) => {
    e.preventDefault();
    if (!selectedUser || !newRole) return;
    try {
      await axios.put(`${API}/users/${selectedUser.id}/role`, { role: newRole }, getAuthHeader());
      toast.success(`Role updated to ${newRole} for ${selectedUser.name}`);
      setRoleChangeOpen(false);
      setSelectedUser(null);
      setNewRole('');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change role');
    }
  };

  const openResetPasswordDialog = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setResetPasswordOpen(true);
  };

  const openRoleChangeDialog = async (user) => {
    setSelectedUser(user);
    setNewRole(user.role);
    // Fetch current admin count
    try {
      const response = await axios.get(`${API}/users/admin-count`, getAuthHeader());
      setAdminCount(response.data.admin_count);
    } catch (error) {
      console.error('Failed to fetch admin count:', error);
      setAdminCount(0);
    }
    setRoleChangeOpen(true);
  };

  const openTabsDialog = (user) => {
    setSelectedUser(user);
    setSelectedTabs(user.allowed_tabs || []);
    setTabsOpen(true);
  };

  const openPermissionsDialog = (user) => {
    setSelectedUser(user);
    setStaffPermissions({
      can_add_order_notification: user.can_add_order_notification || false,
      can_manage_feed_inventory: user.can_manage_feed_inventory || false,
      can_add_fulfilment: user.can_add_fulfilment || false
    });
    setPermissionsOpen(true);
  };

  const handleTabsUpdate = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    try {
      await axios.put(`${API}/users/${selectedUser.id}/tabs`, { allowed_tabs: selectedTabs }, getAuthHeader());
      toast.success(`Tab permissions updated for ${selectedUser.name}`);
      setTabsOpen(false);
      setSelectedUser(null);
      setSelectedTabs([]);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update tab permissions');
    }
  };

  const handlePermissionsUpdate = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    try {
      await axios.put(`${API}/users/${selectedUser.id}/staff-permissions`, staffPermissions, getAuthHeader());
      toast.success(`Function permissions updated for ${selectedUser.name}`);
      setPermissionsOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update permissions');
    }
  };

  const handleToggleDistributorVisibility = async (userId, currentlyHidden) => {
    try {
      await axios.put(`${API}/users/${userId}/toggle-customer-visibility`, {
        hidden_from_customer_dropdown: !currentlyHidden
      }, getAuthHeader());
      toast.success(currentlyHidden 
        ? 'Distributor is now visible to customers' 
        : 'Distributor is now hidden from customers');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update visibility');
    }
  };

  const toggleTab = (tabId) => {
    setSelectedTabs(prev => 
      prev.includes(tabId) 
        ? prev.filter(t => t !== tabId)
        : [...prev, tabId]
    );
  };

  const handleClose = () => {
    setOpen(false);
    setFormData({
      name: '',
      email: '',
      username: '',
      phone: '',
      role: 'staff',
      address: ''
    });
  };

  const getRoleBadge = (role) => {
    const badges = {
      admin: 'bg-red-100 text-red-700 px-2 py-1 rounded-full text-xs font-medium',
      manager: 'bg-amber-100 text-amber-700 px-2 py-1 rounded-full text-xs font-medium',
      staff: 'bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full text-xs font-medium',
      support_staff: 'bg-teal-100 text-teal-700 px-2 py-1 rounded-full text-xs font-medium',
      customer: 'bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium',
      distributor: 'bg-purple-100 text-purple-700 px-2 py-1 rounded-full text-xs font-medium',
      vet_doctor: 'bg-pink-100 text-pink-700 px-2 py-1 rounded-full text-xs font-medium',
      auditor: 'bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full text-xs font-medium'
    };
    return badges[role] || 'bg-slate-100 text-slate-700 px-2 py-1 rounded-full text-xs font-medium';
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Staff Management
          </h1>
          <p className="text-lg text-slate-600 mt-2">Manage staff accounts, roles, permissions and view activity</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-button" className="bg-emerald-600 hover:bg-emerald-700">
              <UserPlus className="w-4 h-4 mr-2" />
              Add Staff
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="user-dialog">
            <DialogHeader>
              <DialogTitle>Add New Staff Member</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Info banner about temporary password */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  <span className="font-semibold">Note:</span> New users will receive temporary password <span className="font-mono bg-blue-100 px-1 rounded">123456</span> and must change it on first login.
                </p>
              </div>
              
              <div>
                <Label htmlFor="name">Full Name <span className="text-red-500">*</span></Label>
                <Input
                  id="name"
                  data-testid="user-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter full name"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="user-email-input"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <Label htmlFor="username">Username</Label>
                  <Input
                    id="username"
                    data-testid="user-username-input"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder="username (for login)"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500 -mt-2">At least one of Email or Username is required for login</p>
              
              <div>
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  data-testid="user-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="e.g., +234..."
                />
              </div>
              <div>
                <Label htmlFor="role">Role</Label>
                <Select onValueChange={(value) => setFormData({ ...formData, role: value })} value={formData.role}>
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="staff">Staff</SelectItem>
                    <SelectItem value="support_staff">Support Staff</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="distributor">Distributor</SelectItem>
                    <SelectItem value="vet_doctor">Vet Doctor</SelectItem>
                    <SelectItem value="auditor">Auditor</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Support Staff: Pay Slip by default, Admin can assign additional tabs
                </p>
              </div>
              <div>
                <Label htmlFor="address">Address (Optional)</Label>
                <Input
                  id="address"
                  data-testid="user-address-input"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Enter address"
                />
              </div>
              <div className="flex gap-2">
                <Button type="submit" data-testid="user-submit-button" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  Create User
                </Button>
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-6 border-b border-slate-200" data-testid="staff-subtabs">
        <button
          onClick={() => setSubTab('staff')}
          data-testid="subtab-staff"
          className={`relative pb-3 text-sm font-semibold transition-colors ${subTab === 'staff' ? 'text-emerald-700' : 'text-slate-400 hover:text-slate-600'}`}
        >
          Staff Accounts
          {subTab === 'staff' && <span className="absolute left-0 -bottom-px h-0.5 w-full bg-emerald-600 rounded-full" />}
        </button>
        <button
          onClick={() => setSubTab('registration')}
          data-testid="subtab-registration"
          className={`relative pb-3 text-sm font-semibold transition-colors ${subTab === 'registration' ? 'text-emerald-700' : 'text-slate-400 hover:text-slate-600'}`}
        >
          Registration Links
          {subTab === 'registration' && <span className="absolute left-0 -bottom-px h-0.5 w-full bg-emerald-600 rounded-full" />}
        </button>
      </div>

      {subTab === 'registration' && (
        <RegistrationLinksPanel />
      )}

      {subTab === 'staff' && (
      <>
      {/* User Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="card p-4 bg-gradient-to-br from-blue-50 to-white border-l-4 border-blue-500">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Users</p>
          <p className="text-3xl font-bold text-slate-900 mt-2">{users.length}</p>
        </div>
        <div className="card p-4 bg-gradient-to-br from-orange-50 to-white border-l-4 border-orange-500">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Pending Approval</p>
          <p className="text-3xl font-bold text-orange-600 mt-2">{users.filter(u => u.is_approved === false).length}</p>
        </div>
        <div className="card p-4 bg-gradient-to-br from-red-50 to-white border-l-4 border-red-500">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Admins</p>
          <p className="text-3xl font-bold text-slate-900 mt-2">{users.filter(u => u.role === 'admin').length}</p>
        </div>
        <div className="card p-4 bg-gradient-to-br from-amber-50 to-white border-l-4 border-amber-500">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Managers</p>
          <p className="text-3xl font-bold text-slate-900 mt-2">{users.filter(u => u.role === 'manager').length}</p>
        </div>
        <div className="card p-4 bg-gradient-to-br from-emerald-50 to-white border-l-4 border-emerald-500">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Staff</p>
          <p className="text-3xl font-bold text-slate-900 mt-2">{users.filter(u => u.role === 'staff').length}</p>
        </div>
        <div className="card p-4 bg-gradient-to-br from-teal-50 to-white border-l-4 border-teal-500">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Support Staff</p>
          <p className="text-3xl font-bold text-slate-900 mt-2">{users.filter(u => u.role === 'support_staff').length}</p>
        </div>
      </div>

      {/* Registration Links panel */}

      {/* Approval details dialog */}
      <Dialog open={!!approveTarget} onOpenChange={(v) => !v && setApproveTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Approve Registration</DialogTitle>
          </DialogHeader>
          {approveTarget && (
            <div className="space-y-4">
              <p className="text-sm text-slate-500">Review the details captured at sign-up before granting access.</p>
              <div className="rounded-xl border border-slate-200 divide-y" data-testid="approve-details">
                <DetailRow icon={UserIcon} label="Surname" value={approveTarget.last_name || '—'} />
                <DetailRow icon={UserIcon} label="First Name" value={approveTarget.first_name || '—'} />
                <DetailRow icon={UserIcon} label="Full Name" value={approveTarget.name} />
                <DetailRow icon={Shield} label="Role" value={(approveTarget.role || '').replace('_', ' ')} capitalize />
                <DetailRow icon={Phone} label="Phone" value={approveTarget.phone || '—'} />
                <DetailRow icon={Mail} label="Email" value={approveTarget.email || '—'} />
                <DetailRow icon={UserIcon} label="Username" value={approveTarget.username || '—'} />
                <DetailRow icon={Calendar} label="Registered" value={approveTarget.created_at ? formatDate(approveTarget.created_at) : '—'} />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setApproveTarget(null)}>Cancel</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700 gap-2" onClick={() => handleApproveUser(approveTarget.id, approveTarget.name)} data-testid="confirm-approve-btn">
                  <CheckCircle className="w-4 h-4" /> Approve &amp; Grant Access
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <div className="card">
        <div className="overflow-x-auto">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Username/Email</th>
                <th>Phone</th>
                <th>Role</th>
                <th>Online</th>
                <th>Last Login</th>
                <th>Approval</th>
                <th>Production Entry</th>
                <th>Data Entries</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody data-testid="users-table-body">
              {users.map((user) => (
                <Fragment key={user.id}>
                <tr>
                  <td className="font-medium">{user.name}</td>
                  <td>{user.username || user.email}</td>
                  <td>{user.phone || '-'}</td>
                  <td>
                    <span className={getRoleBadge(user.role)}>
                      {user.role}
                    </span>
                  </td>
                  <td>
                    {onlineStatus[user.id]?.is_active ? (
                      <span className="inline-flex items-center gap-1.5 text-emerald-600">
                        <Circle className="w-2.5 h-2.5 fill-emerald-500" />
                        <span className="text-xs font-medium">Active</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-slate-400">
                        <Circle className="w-2.5 h-2.5 fill-slate-300" />
                        <span className="text-xs font-medium">Offline</span>
                      </span>
                    )}
                  </td>
                  <td>
                    {onlineStatus[user.id]?.last_activity ? (
                      <span className="text-xs text-slate-600">
                        {formatDateTime(onlineStatus[user.id].last_activity)}
                      </span>
                    ) : (
                      <span className="text-xs text-slate-400">Never</span>
                    )}
                  </td>
                  <td>
                    {user.is_approved !== false ? (
                      <span className="bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Approved
                      </span>
                    ) : (
                      <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded-full text-xs font-medium inline-flex items-center gap-1">
                        <XCircle className="w-3 h-3" />
                        Pending
                      </span>
                    )}
                  </td>
                  <td>
                    <Switch
                      checked={user.can_enter_production || user.role === 'admin' || user.role === 'manager'}
                      onCheckedChange={() => handlePermissionToggle(user.id, user.can_enter_production)}
                      disabled={user.role === 'admin' || user.role === 'manager'}
                      data-testid={`permission-toggle-${user.id}`}
                    />
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <Activity className="w-4 h-4 text-slate-400" />
                      <span className="font-medium text-blue-600">
                        {userActivities[user.id]?.total_entries || 0}
                      </span>
                      <span className="text-xs text-slate-500">
                        (P: {userActivities[user.id]?.production_entries || 0}, 
                        S: {userActivities[user.id]?.sales_entries || 0}, 
                        F: {userActivities[user.id]?.feed_received_entries || 0})
                      </span>
                    </div>
                  </td>
                  <td>{formatDate(user.created_at)}</td>
                  <td>
                    <div className="flex gap-1">
                      {/* Expand HR / employment details */}
                      <button
                        data-testid={`expand-user-${user.id}`}
                        onClick={() => setExpandedUserId(expandedUserId === user.id ? null : user.id)}
                        className="p-2 text-slate-600 hover:bg-slate-100 rounded transition-colors"
                        title="View employment details"
                      >
                        {expandedUserId === user.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                      {/* Edit button - available for all non-admin users */}
                      {user.role !== 'admin' && (
                        <button
                          data-testid={`edit-user-${user.id}`}
                          onClick={() => openEditDialog(user)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Edit User Details"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      )}
                      {/* Approval buttons */}
                      {user.is_approved === false && user.role !== 'admin' && (
                        <button
                          data-testid={`approve-user-${user.id}`}
                          onClick={() => setApproveTarget(user)}
                          className="p-2 text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                          title="Approve User"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      )}
                      {user.is_approved !== false && user.role !== 'admin' && (
                        <button
                          data-testid={`revoke-user-${user.id}`}
                          onClick={() => handleRevokeApproval(user.id, user.name)}
                          className="p-2 text-orange-600 hover:bg-orange-50 rounded transition-colors"
                          title="Revoke Approval"
                        >
                          <XCircle className="w-4 h-4" />
                        </button>
                      )}
                      {user.role === 'staff' && (
                        <button
                          data-testid={`staff-permissions-${user.id}`}
                          onClick={() => openPermissionsDialog(user)}
                          className="p-2 text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                          title="Function Permissions"
                        >
                          <Settings2 className="w-4 h-4" />
                        </button>
                      )}
                      {/* Hide from customer dropdown toggle for distributors */}
                      {user.role === 'distributor' && (
                        <button
                          data-testid={`toggle-visibility-${user.id}`}
                          onClick={() => handleToggleDistributorVisibility(user.id, user.hidden_from_customer_dropdown)}
                          className={`p-2 rounded transition-colors ${
                            user.hidden_from_customer_dropdown 
                              ? 'text-red-600 hover:bg-red-50' 
                              : 'text-slate-400 hover:bg-slate-100'
                          }`}
                          title={user.hidden_from_customer_dropdown 
                            ? 'Hidden from customers - click to show' 
                            : 'Visible to customers - click to hide'}
                        >
                          {user.hidden_from_customer_dropdown ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      )}
                      {/* Tab access management - available for all non-admin users */}
                      {user.role !== 'admin' && (
                        <button
                          data-testid={`assign-tabs-${user.id}`}
                          onClick={() => openTabsDialog(user)}
                          className={`p-2 hover:bg-purple-50 rounded transition-colors ${
                            user.allowed_tabs && user.allowed_tabs.length > 0 
                              ? 'text-purple-600' 
                              : 'text-slate-400'
                          }`}
                          title={`Manage Tab Access${user.allowed_tabs?.length > 0 ? ` (${user.allowed_tabs.length} tabs assigned)` : ' (click to assign tabs)'}`}
                        >
                          <LayoutGrid className="w-4 h-4" />
                        </button>
                      )}
                      {user.role === 'staff' && (
                        <button
                          data-testid={`assign-permissions-${user.id}`}
                          onClick={() => openPermissionsDialog(user)}
                          className="p-2 text-cyan-600 hover:bg-cyan-50 rounded transition-colors"
                          title="Assign Function Permissions"
                        >
                          <Settings2 className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        data-testid={`change-role-${user.id}`}
                        onClick={() => openRoleChangeDialog(user)}
                        className="p-2 text-amber-600 hover:bg-amber-50 rounded transition-colors"
                        title="Change Role"
                      >
                        <Shield className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`reset-password-${user.id}`}
                        onClick={() => openResetPasswordDialog(user)}
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Reset Password"
                      >
                        <Key className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`delete-user-${user.id}`}
                        onClick={() => handleDelete(user.id)}
                        className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                        title="Delete User"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedUserId === user.id && (
                  <tr key={`${user.id}-details`} className="bg-slate-50" data-testid={`user-details-row-${user.id}`}>
                    <td colSpan={11} className="p-4">
                      <div className="flex flex-col md:flex-row gap-6">
                        <div className="shrink-0">
                          {user.profile_photo ? (
                            <img src={user.profile_photo} alt={user.name} className="w-20 h-20 rounded-full object-cover border" />
                          ) : (
                            <div className="w-20 h-20 rounded-full bg-slate-200 flex items-center justify-center text-slate-400 border">
                              <UserIcon className="w-8 h-8" />
                            </div>
                          )}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2 text-sm flex-1">
                          <div><span className="text-slate-400">Date of Hire:</span> <span className="font-medium">{user.date_of_hire ? formatDate(user.date_of_hire) : '—'}</span></div>
                          <div><span className="text-slate-400">Length of Service:</span> <span className="font-medium">{lengthOfService(user.date_of_hire)}</span></div>
                          <div><span className="text-slate-400">Starting Salary:</span> <span className="font-medium">{(user.starting_salary !== null && user.starting_salary !== undefined) ? `₦${Number(user.starting_salary).toLocaleString()}` : '—'}</span></div>
                          <div><span className="text-slate-400">Next of Kin:</span> <span className="font-medium">{user.next_of_kin_name || '—'}</span></div>
                          <div><span className="text-slate-400">Next of Kin Phone:</span> <span className="font-medium">{user.next_of_kin_phone || '—'}</span></div>
                          <div><span className="text-slate-400">Address:</span> <span className="font-medium">{user.address || '—'}</span></div>
                          <div><span className="text-slate-400">Emergency Contact:</span> <span className="font-medium">{user.emergency_contact_name || '—'}</span></div>
                          <div><span className="text-slate-400">Emergency Phone:</span> <span className="font-medium">{user.emergency_contact_phone || '—'}</span></div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
          {users.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              No users found.
            </div>
          )}
        </div>
      </div>
      </>
      )}

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordOpen} onOpenChange={setResetPasswordOpen}>
        <DialogContent data-testid="reset-password-dialog">
          <DialogHeader>
            <DialogTitle>Reset Password for {selectedUser?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                data-testid="new-password-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                placeholder="Enter new password (min 6 characters)"
              />
            </div>
            <div className="flex gap-2">
              <Button type="submit" data-testid="reset-password-submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                Reset Password
              </Button>
              <Button type="button" variant="outline" onClick={() => setResetPasswordOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog open={roleChangeOpen} onOpenChange={setRoleChangeOpen}>
        <DialogContent data-testid="role-change-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-amber-600" />
              Change Role for {selectedUser?.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRoleChange} className="space-y-4">
            <div>
              <Label htmlFor="current-role" className="text-slate-500">Current Role</Label>
              <div className="mt-1 px-3 py-2 bg-slate-100 rounded-md text-sm font-medium capitalize">
                {selectedUser?.role?.replace('_', ' ')}
              </div>
            </div>
            
            <div>
              <Label htmlFor="new-role">New Role *</Label>
              <Select onValueChange={(value) => setNewRole(value)} value={newRole}>
                <SelectTrigger data-testid="new-role-select" className="mt-1">
                  <SelectValue placeholder="Select new role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="staff">Staff</SelectItem>
                  <SelectItem value="support_staff">Support Staff</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem 
                    value="admin" 
                    disabled={adminCount >= MAX_ADMINS && selectedUser?.role !== 'admin'}
                  >
                    Admin {adminCount >= MAX_ADMINS && selectedUser?.role !== 'admin' && '(Limit Reached)'}
                  </SelectItem>
                  <SelectItem value="distributor">Distributor</SelectItem>
                  <SelectItem value="vet_doctor">Vet Doctor</SelectItem>
                  <SelectItem value="auditor">Auditor</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Admin limit warning */}
              {newRole === 'admin' && adminCount >= MAX_ADMINS && selectedUser?.role !== 'admin' && (
                <p className="text-sm text-red-600 mt-2 flex items-center gap-1">
                  <XCircle className="w-4 h-4" />
                  Maximum of {MAX_ADMINS} Admin users allowed ({adminCount}/{MAX_ADMINS})
                </p>
              )}
              
              {/* Admin count info */}
              <p className="text-xs text-slate-500 mt-2">
                Current Admins: {adminCount}/{MAX_ADMINS}
              </p>
            </div>
            
            <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1.5">
              <p className="font-medium text-slate-700 mb-2">Role Permissions:</p>
              <p><span className="font-medium text-blue-600">Staff:</span> Production, Sales, Inventory, Messages</p>
              <p><span className="font-medium text-purple-600">Support Staff:</span> Pay Slip + Admin-assigned tabs</p>
              <p><span className="font-medium text-emerald-600">Manager:</span> Staff access + Dashboard, Customers, Payroll, Reports</p>
              <p><span className="font-medium text-amber-600">Admin:</span> Full access including Staff Management</p>
              <p><span className="font-medium text-orange-600">Distributor:</span> Sales, Orders, Payments</p>
              <p><span className="font-medium text-pink-600">Vet Doctor:</span> Vet Reports, Messages</p>
            </div>
            
            <div className="flex gap-2 pt-2">
              <Button 
                type="submit" 
                data-testid="role-change-submit" 
                className="flex-1 bg-amber-600 hover:bg-amber-700"
                disabled={!newRole || newRole === selectedUser?.role || (newRole === 'admin' && adminCount >= MAX_ADMINS && selectedUser?.role !== 'admin')}
              >
                Update Role
              </Button>
              <Button type="button" variant="outline" onClick={() => setRoleChangeOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Tab Assignment Dialog */}
      <Dialog open={tabsOpen} onOpenChange={setTabsOpen}>
        <DialogContent data-testid="tabs-dialog" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Manage Tab Access for {selectedUser?.name}</DialogTitle>
            <p className="text-sm text-slate-500">
              Role: <span className="font-medium capitalize">{selectedUser?.role?.replace('_', ' ')}</span>
              <br/>
              Select which tabs this user can access. If none selected, user gets default access for their role.
            </p>
          </DialogHeader>
          <form onSubmit={handleTabsUpdate} className="space-y-4">
            <div className="space-y-3 max-h-64 overflow-y-auto border rounded-lg p-3 bg-slate-50">
              {selectedUser && getAvailableTabsForRole(selectedUser.role).length > 0 ? (
                getAvailableTabsForRole(selectedUser.role).map((tab) => (
                  <div key={tab.id} className="flex items-center space-x-3 p-2 hover:bg-white rounded bg-white/50">
                    <Checkbox
                      id={`tab-${tab.id}`}
                      checked={selectedTabs.includes(tab.id)}
                      onCheckedChange={() => toggleTab(tab.id)}
                    />
                    <label
                      htmlFor={`tab-${tab.id}`}
                      className="text-sm font-medium cursor-pointer flex-1"
                    >
                      {tab.label}
                    </label>
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-500 text-center py-4">
                  No configurable tabs available for this role.
                </p>
              )}
            </div>
            
            {selectedTabs.length > 0 && (
              <div className="bg-purple-50 p-3 rounded-lg">
                <p className="text-xs font-medium text-purple-700 mb-1">Restricted to {selectedTabs.length} tab(s):</p>
                <div className="flex flex-wrap gap-1">
                  {selectedTabs.map(tabId => {
                    const tab = AVAILABLE_TABS.find(t => t.id === tabId);
                    return (
                      <span key={tabId} className="bg-purple-100 text-purple-800 text-xs px-2 py-0.5 rounded">
                        {tab?.label || tabId}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
            
            <div className="flex gap-2">
              <Button type="submit" data-testid="tabs-submit" className="flex-1 bg-purple-600 hover:bg-purple-700">
                Save Tab Access
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => {
                  setSelectedTabs([]);
                }}
                className="text-slate-600"
              >
                Clear All
              </Button>
              <Button type="button" variant="outline" onClick={() => setTabsOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Staff Function Permissions Dialog */}
      <Dialog open={permissionsOpen} onOpenChange={setPermissionsOpen}>
        <DialogContent data-testid="permissions-dialog">
          <DialogHeader>
            <DialogTitle>Function Permissions for {selectedUser?.name}</DialogTitle>
            <p className="text-sm text-slate-500">Control which specific functions this staff member can perform.</p>
          </DialogHeader>
          <form onSubmit={handlePermissionsUpdate} className="space-y-4">
            <div className="space-y-4">
              {STAFF_PERMISSIONS.map((perm) => (
                <div key={perm.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <label
                    htmlFor={`perm-${perm.id}`}
                    className="text-sm font-medium cursor-pointer flex-1"
                  >
                    {perm.label}
                  </label>
                  <Switch
                    id={`perm-${perm.id}`}
                    checked={staffPermissions[perm.id]}
                    onCheckedChange={(checked) => setStaffPermissions(prev => ({...prev, [perm.id]: checked}))}
                  />
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <Button type="submit" data-testid="permissions-submit" className="flex-1 bg-cyan-600 hover:bg-cyan-700">
                Update Permissions
              </Button>
              <Button type="button" variant="outline" onClick={() => setPermissionsOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Details Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-user-dialog" className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit User Details</DialogTitle>
            <p className="text-sm text-slate-500">
              Update contact information for <span className="font-medium">{editingUser?.name}</span>
              <br/>
              <span className="text-xs capitalize">Role: {editingUser?.role?.replace('_', ' ')}</span>
            </p>
          </DialogHeader>
          <form onSubmit={handleEditSubmit} className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Full Name</Label>
              <Input
                id="edit-name"
                data-testid="edit-name-input"
                value={editFormData.name}
                onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                placeholder="Enter full name"
              />
            </div>
            <div>
              <Label htmlFor="edit-phone">Phone Number</Label>
              <Input
                id="edit-phone"
                data-testid="edit-phone-input"
                value={editFormData.phone}
                onChange={(e) => setEditFormData({...editFormData, phone: e.target.value})}
                placeholder="Enter phone number"
              />
            </div>
            <div>
              <Label htmlFor="edit-email">Email Address</Label>
              <Input
                id="edit-email"
                type="email"
                data-testid="edit-email-input"
                value={editFormData.email}
                onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                placeholder="Enter email address"
              />
            </div>
            <div>
              <Label htmlFor="edit-address">Address</Label>
              <Input
                id="edit-address"
                data-testid="edit-address-input"
                value={editFormData.address}
                onChange={(e) => setEditFormData({...editFormData, address: e.target.value})}
                placeholder="Enter address"
              />
            </div>
            <div>
              <Label htmlFor="edit-date-of-hire">Date of Hire</Label>
              <Input
                id="edit-date-of-hire"
                type="date"
                data-testid="edit-date-of-hire-input"
                value={editFormData.date_of_hire}
                onChange={(e) => setEditFormData({...editFormData, date_of_hire: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="edit-nok-name">Next of Kin - Full Name</Label>
                <Input
                  id="edit-nok-name"
                  data-testid="edit-nok-name-input"
                  value={editFormData.next_of_kin_name}
                  onChange={(e) => setEditFormData({...editFormData, next_of_kin_name: e.target.value})}
                  placeholder="Enter next of kin full name"
                />
              </div>
              <div>
                <Label htmlFor="edit-nok-phone">Next of Kin - Contact Number</Label>
                <Input
                  id="edit-nok-phone"
                  data-testid="edit-nok-phone-input"
                  value={editFormData.next_of_kin_phone}
                  onChange={(e) => setEditFormData({...editFormData, next_of_kin_phone: e.target.value})}
                  placeholder="Enter next of kin phone"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="edit-starting-salary">Starting Salary (₦)</Label>
              <Input
                id="edit-starting-salary"
                type="number"
                min="0"
                step="0.01"
                data-testid="edit-starting-salary-input"
                value={editFormData.starting_salary}
                onChange={(e) => setEditFormData({...editFormData, starting_salary: e.target.value})}
                placeholder="Enter starting salary"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="edit-emergency-name">Emergency Contact - Name</Label>
                <Input
                  id="edit-emergency-name"
                  data-testid="edit-emergency-name-input"
                  value={editFormData.emergency_contact_name}
                  onChange={(e) => setEditFormData({...editFormData, emergency_contact_name: e.target.value})}
                  placeholder="Emergency contact person"
                />
              </div>
              <div>
                <Label htmlFor="edit-emergency-phone">Emergency Contact - Telephone</Label>
                <Input
                  id="edit-emergency-phone"
                  data-testid="edit-emergency-phone-input"
                  value={editFormData.emergency_contact_phone}
                  onChange={(e) => setEditFormData({...editFormData, emergency_contact_phone: e.target.value})}
                  placeholder="Emergency contact number"
                />
              </div>
            </div>
            <div>
              <Label>Employee Photo</Label>
              <div className="flex items-center gap-4 mt-1">
                {editFormData.profile_photo ? (
                  <img src={editFormData.profile_photo} alt="Employee" className="w-16 h-16 rounded-full object-cover border" data-testid="edit-photo-preview" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 border">
                    <UserIcon className="w-7 h-7" />
                  </div>
                )}
                <div className="flex gap-2">
                  <Input
                    id="edit-photo"
                    type="file"
                    accept="image/*"
                    data-testid="edit-photo-input"
                    onChange={handleEditPhotoChange}
                    className="max-w-[220px]"
                  />
                  {editFormData.profile_photo && (
                    <Button type="button" variant="outline" size="sm" onClick={() => setEditFormData({...editFormData, profile_photo: ''})} data-testid="edit-photo-remove">
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button type="submit" data-testid="edit-user-submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                Save Changes
              </Button>
              <Button type="button" variant="outline" onClick={() => setEditOpen(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
